import { useLazyQuery, useQuery } from '@apollo/client';
import {
  Badge,
  Box,
  Tag,
  Button,
  Stack,
  useColorModeValue,
  Flex,
  Text,
  Switch,
} from '@chakra-ui/react';

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link , useSearchParams, useNavigate  } from 'react-router-dom';
import { tableAtom } from '../../components/ListView/atoms';
import HeaderWithActions from '../../components/HeaderWithActions';
import ListView from '../../components/ListView';
import Meta from '../../components/Meta';
import debounce from 'lodash.debounce';

import {
  DELETE,
  CUSTOM_FETCH_ALL_STRIPE,
  CUSTOM_FETCH_NO_STRIPE,
} from './graphql';
import { setup } from './setup';
import dayjs from 'dayjs';
import { useRecoilState } from 'recoil';

const columns = [
  {
    Header: '',
    accessor: '_id',
    disableSortBy: true,
    Cell: ({ row }) => (
      <Link to={`/${setup.model}/edit/${row.original[setup.accessor]}`}>
        <Button variant='link'>View</Button>
      </Link>
    ),
  },
  {
    Header: 'Customer',
    accessor: 'email',
    disableSortBy: true,
    Cell: ({ value, row }) => (
      <Flex direction={'column'}>
        <Flex direction={'row'}>
          <Text mr='1ch'>{row?.original?.firstName}</Text>
          <Text>{row?.original?.lastName}</Text>
        </Flex>
        <Text
          as='a'
          target='_blank'
          href={`mailto:${value}`}
          textColor={'blue.400'}
          fontWeight={'bold'}
        >
          {value}
        </Text>
      </Flex>
    ),
  },

  {
    Header: 'Email Validation',
    accessor: 'emailValidation',
    disableSortBy: false,
    Cell: ({ value }) => (
      <div>
        {value?.isValidated && (
          <Tag colorScheme='green' textAlign='center'>
            Validated
          </Tag>
        )}
      </div>
    ),
  },
  {
    Header: 'Registration Date',
    accessor: 'createdAt',
    disableSortBy: true,
    Cell: ({ value }) => dayjs(value).format('DD/MM/YYYY'),
  },
  {
    Header: 'Subscription',
    accessor: 'stripe',
    disableSortBy: true,
    Cell: ({ row, value }) => (
      <Flex direction={'column'}>
        <Badge colorScheme='green' textAlign='center'>
          {value?.subscription?.product?.metadata?.sub_type}
        </Badge>

        <Text>{value?.subscription?.customer}</Text>
      </Flex>
    ),
  },
  {
    Header: 'Status',
    accessor: 'is_active',
    disableSortBy: false,
    Cell: ({ value }) =>
      value === true ? (
        <Badge colorScheme='green' width='9ch' textAlign='center'>
          ACTIVE
        </Badge>
      ) : (
        <Badge colorScheme='red' variant='solid' width='9ch' textAlign='center'>
          INACTIVE
        </Badge>
      ),
  },
  {
    Header: 'Suspended',
    accessor: 'isSuspended',
    disableSortBy: false,
    Cell: ({ value }) =>
      value === true ? (
        <Badge
          colorScheme='red'
          width='fit-content'
          variant='solid'
          textAlign='center'
        >
          Account Suspended
        </Badge>
      ) : null,
  },
];

const filters = [
  {
    value: 'last_name',
    type: 'text',
    label: 'Last Name',
    isOperator: false,
  },
];

const List = ({ title }) => {
  const [fetchData, { loading, error, data }] = useLazyQuery(CUSTOM_FETCH_NO_STRIPE);
  const [fetchStripeData, { loading: stripeLoading, error: stripeError, data: stripeData }] = useLazyQuery(CUSTOM_FETCH_ALL_STRIPE);

  const [searchParams, setSearchParams] = useSearchParams();
  const [tableState, setTableState] = useRecoilState(tableAtom);
  const [loadStripe, setLoadStripe] = useState(false);
  const [showSuspended, setShowSuspended] = useState(searchParams.get("suspended") === "true");
  const [showVerified, setShowVerified] = useState(searchParams.get("verified") === "true");
  const [placeId, setPlaceId] = useState(searchParams.get("placeId") || null);
  const [searchString, setSearchString] = useState(searchParams.get("searchFilter") || "");
  const [filterDrawer, setFilterDrawer] = useState([]);

  const navigate = useNavigate();

  const delayedSetSearchParams = (params) => {
    setTimeout(() => setSearchParams(params), 0);
  };

  const updateFiltersSafely = (updateFn) => {
    setTableState((old) => ({ ...old, hydrated: "" }));
    searchParams.set("page", "1");
    searchParams.set("limit", "20");
    setTimeout(()=>{
      updateFn();
      setTableState((old) => ({
        ...old,
        params: {
          ...old.params,
          page: 1,
          skip: 0,
          pagination: 20,
        },
        hydrated: setup.model,
      }))}, 0);
  };

  const confirm = (val) => {
    updateFiltersSafely(() => {
      setSearchString(val);
      if (val) {
        searchParams.set("searchFilter", val);
      } else {
        searchParams.delete("searchFilter");
      }
      delayedSetSearchParams(searchParams);
    });
  };

  const handlePlaceId = (val) => {
    updateFiltersSafely(() => {
      setPlaceId(val);
      if (val) {
        searchParams.set("placeId", val);
      } else {
        searchParams.delete("placeId");
      }
      delayedSetSearchParams(searchParams);
    });
  };

  const _searchFunction = useCallback(debounce(confirm, 500), [searchString]);

  const buildFetchFilters = () => {
    const filter = {};
    if (showSuspended) filter.isSuspended = true;
    if (showVerified) filter.isVerified = true;
    if (placeId) filter.places = [{ _id: placeId }];
    if (searchString) {
      filter.OR = [
        { firstName: searchString },
        { lastName: searchString },
        { email: searchString },
      ];
    }
    return { filter };
  };

  const fetchFilters = useMemo(buildFetchFilters, [showSuspended, showVerified, placeId, searchString]);

  return (
    <Meta title={setup.title}>
      <HeaderWithActions title={setup.title}>
        <Stack
          ml={'auto'}
          isInline
          spacing='15px'
          w='fit-content'
          alignItems={'center'}
        >
          {setup.canAddNew && (
            <Link to={`/${setup.model}/create`}>
              <Button>Add {setup.singular}</Button>
            </Link>
          )}

          <Button onClick={() => setLoadStripe(!loadStripe)} w='100%'>
            {loadStripe ? 'Hide' : 'Show'} Subscription details
          </Button>
          <Flex direction={'row'} w='100%' whiteSpace={'nowrap'} gap='10px'>
            <Text>Show Suspended Users</Text>
            <Switch
              isChecked={showSuspended}
             onChange={() => {
                updateFiltersSafely(() => {
                  const value = !showSuspended;
                  setShowSuspended(value);
                  setShowVerified(false);
                  if (value) searchParams.set("suspended", "true"); else searchParams.delete("suspended");
                  searchParams.delete("verified");
                  delayedSetSearchParams(searchParams);
                });
              }}
            />
          </Flex>
          <Flex direction={'row'} w='100%' whiteSpace={'nowrap'} gap='10px'>
            <Text>Show Verified Users</Text>
           <Switch
              isChecked={showVerified}
              onChange={() => {
               updateFiltersSafely(() => {
                  const value = !showVerified;
                  setShowVerified(value);
                  setShowSuspended(false);
                  if (value) searchParams.set("verified", "true"); else searchParams.delete("verified");
                  searchParams.delete("suspended");
                  delayedSetSearchParams(searchParams);
                });
              }}
            />
          </Flex>
        </Stack>
      </HeaderWithActions>
      <Box
        mt='25px'
        bg={useColorModeValue('white', 'gray.700')}
        shadow='base'
        rounded={{
          base: 'md',
          sm: 'xl',
        }}
        w='100%'
      >
        <ListView
          searchForPlace={handlePlaceId}
          setup={setup}
          fetchData={loadStripe ? fetchStripeData : fetchData}
          // setExternalSearch={_searchFunction}
          data={
            stripeData?.businessusers_List
              ? stripeData?.businessusers_List
              : data?.businessusers_List
          }
          loading={loading}
          // setSearchParam={setSearchString}
          error={error}
          fetchFilters={fetchFilters}
          gqlDelete={DELETE}
          setFilterDrawer={setFilterDrawer}
          columns={columns}
          filters={filters}
          customVariables={
            loadStripe && {
              custom: {
                stripe: {
                  sub_data: true,
                },
              },
            }
          }

          // searchString={searchString}
        />
      </Box>
    </Meta>
  );
};

export default List;
